import { useCallback, useState } from 'react';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { cn } from '../../../utils/className';
import { Skeleton } from '../../Skeleton';
import { CompactTable } from './CompactTable';
import { MobileListHeader } from './MobileListHeader';
import { MobilePagination } from './MobilePagination';
import { MobileListProps } from './types';

/**
 * MobileList - Generic mobile list component
 * Renders a scrollable list of items optimized for touch interfaces
 */
export function MobileList<TData>({
  data,
  isLoading = false,
  onRefresh,
  getRowId,
  renderItem,
  enableSelection = false,
  selectedIds = [],
  onSelectionChange,
  selectionMode: controlledSelectionMode,
  onSelectionModeChange,
  onItemClick,
  title,
  icon,
  headerActions,
  emptyState,
  ariaLabel = 'List',
  currentPage,
  totalPages,
  totalCount,
  rowsPerPage,
  onPageChange,
  layout = 'list',
  gridClassName,
  columns,
  sort,
  getRowLabel,
  activeId,
}: MobileListProps<TData>) {
  const [ownSelectionMode, setOwnSelectionMode] = useState(false);
  const selectionMode = controlledSelectionMode ?? ownSelectionMode;
  const setSelectionMode = onSelectionModeChange ?? setOwnSelectionMode;

  // Handle item selection
  const handleSelect = useCallback(
    (id: string) => {
      if (!onSelectionChange) return;

      const isSelected = selectedIds.includes(id);
      const newSelectedIds = isSelected
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id];

      onSelectionChange(newSelectedIds);
    },
    [selectedIds, onSelectionChange]
  );

  // Handle item click
  const handleItemClick = useCallback(
    (item: TData) => {
      if (selectionMode) {
        // In selection mode, clicking selects/deselects
        handleSelect(getRowId(item));
      } else {
        // Normal mode, trigger onClick handler
        onItemClick?.(item);
      }
    },
    [selectionMode, handleSelect, onItemClick, getRowId]
  );

  // Toggle selection mode
  const toggleSelectionMode = useCallback(() => {
    if (selectionMode && onSelectionChange) {
      // Exiting selection mode, clear selections
      onSelectionChange([]);
    }
    setSelectionMode(!selectionMode);
  }, [selectionMode, setSelectionMode, onSelectionChange]);

  // Clear selection
  const handleClearSelection = useCallback(() => {
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  }, [onSelectionChange]);

  const items =
    layout === 'table' && columns ? (
      <CompactTable
        data={data}
        columns={columns}
        getRowId={getRowId}
        getRowLabel={getRowLabel}
        selectionMode={selectionMode}
        selectedIds={selectedIds}
        onSelect={handleSelect}
        onItemClick={handleItemClick}
        activeId={activeId}
        sort={sort}
      />
    ) : (
      <div
        className={cn(
          'max-w-full',
          layout === 'grid'
            ? cn(
                'grid gap-x-4 gap-y-8 p-4',
                gridClassName ??
                  `grid-cols-1 @[30rem]:grid-cols-2 @[48rem]:grid-cols-3
                    @[64rem]:grid-cols-4`
              )
            : 'divide-border-secondary divide-y'
        )}
        role='list'
      >
        {data.map((item) => {
          const id = getRowId(item);

          return (
            <div
              key={id}
              role='listitem'
              className='max-w-full min-w-0 overflow-hidden'
            >
              {renderItem(item, {
                isSelected: selectedIds.includes(id),
                selectionMode,
                onSelect: handleSelect,
                onClick: handleItemClick,
                isActive: id === activeId,
              })}
            </div>
          );
        })}
      </div>
    );

  // Loading state
  if (isLoading) {
    return (
      <div
        className='bg-surface-primary @container max-w-full'
        role='region'
        aria-label={ariaLabel}
      >
        {title && (
          <MobileListHeader
            title={title}
            icon={icon}
            headerActions={headerActions}
            enableSelection={enableSelection}
          />
        )}
        <div className='divide-border-secondary max-w-full divide-y'>
          {[...Array(5)].map((_, i) => (
            <div key={i} className='flex items-center gap-4 p-4'>
              <Skeleton className='h-14 w-14 shrink-0' />
              <div className='flex-1'>
                <Skeleton className='mb-2 h-4 w-3/4' />
                <Skeleton className='h-3 w-1/2' />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div
        className='bg-surface-primary @container max-w-full'
        role='region'
        aria-label={ariaLabel}
      >
        {title && (
          <MobileListHeader
            title={title}
            icon={icon}
            headerActions={headerActions}
            enableSelection={enableSelection}
          />
        )}
        <div className='flex items-center justify-center py-12'>
          {emptyState || (
            <div className='text-secondary text-center'>
              <p className='size-p1 font-medium'>No items</p>
              <p className='size-p2'>No data to display</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className='bg-surface-primary @container max-w-full'
      role='region'
      aria-label={ariaLabel}
    >
      {/* Header */}
      {title && (
        <MobileListHeader
          title={title}
          icon={icon}
          headerActions={
            enableSelection ? (
              <>
                <button
                  onClick={toggleSelectionMode}
                  className={`hover:bg-surface-focus-support border-border-focus
                    text-focus size-p2 hover:text-focus-strong cursor-pointer
                    rounded border px-3 py-1.5 font-medium transition-colors`}
                >
                  {selectionMode ? 'Done' : 'Select'}
                </button>
                {headerActions}
              </>
            ) : (
              headerActions
            )
          }
          enableSelection={enableSelection}
          selectedIds={selectedIds}
          onClearSelection={selectionMode ? handleClearSelection : undefined}
        />
      )}

      {/* Pull-to-refresh wrapper for the list content */}
      {onRefresh ? (
        <PullToRefresh
          onRefresh={onRefresh}
          className={layout === 'table' ? 'ptr--sticky-header' : ''}
          pullingContent={
            <div
              className='text-secondary size-p2 flex items-center justify-center
                py-2'
            >
              ↓ Pull to refresh
            </div>
          }
          refreshingContent={
            <div
              className='text-secondary size-p2 flex items-center justify-center
                gap-2 py-2'
            >
              <svg
                className='h-4 w-4 animate-spin'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                />
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                />
              </svg>
              Refreshing...
            </div>
          }
        >
          {items}
        </PullToRefresh>
      ) : (
        items
      )}

      {/* Pagination footer (mobile) */}
      {typeof currentPage === 'number' &&
        typeof totalPages === 'number' &&
        onPageChange && (
          <div className='border-border-secondary border-t'>
            <MobilePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              rowsPerPage={rowsPerPage}
              onPageChange={onPageChange}
              isLoading={isLoading}
            />
          </div>
        )}
    </div>
  );
}
